import { Avatar, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslate } from "ra-core";
import { useSidebarState } from "react-admin";
import { imageURL } from "../http";

/**
 * A button that toggles the sidebar. Used by default in the <AppBar>.
 * @param props The component props
 * @param {String} props.className An optional class name to apply to the button
 */
export const SidebarToggleButton = (props: SidebarToggleButtonProps): JSX.Element => {
    const translate = useTranslate();

    const { className } = props;
    const [open, setOpen] = useSidebarState();
    return (
        <Tooltip
            className={className}
            title={props.title ?? translate(open ? "ra.action.close_menu" : "ra.action.open_menu", { _: "Open/Close menu" })}
            enterDelay={500}
        >
            <StyledIconButton onClick={() => setOpen(!open)}>
                <Avatar
                    sx={t => ({ color: t.palette.text.secondary, height: 48, width: 56, bgcolor: "transparent", padding: "0 4px", "& .MuiAvatar-img": { objectFit: "contain" } })}
                    src={props.image ? imageURL(props.image) : undefined}
                    alt={props.title}
                    classes={{
                        root: open ? SidebarToggleButtonClasses.menuButtonIconOpen : SidebarToggleButtonClasses.menuButtonIconClosed,
                    }}
                >
                    {props.title?.slice(0, 1)}
                </Avatar>
            </StyledIconButton>
        </Tooltip>
    );
};

export type SidebarToggleButtonProps = {
    className?: string;
    image?: string;
    title?: string;
};

const PREFIX = "RaSidebarToggleButton";

const SidebarToggleButtonClasses = {
    menuButtonIconClosed: `${PREFIX}-menuButtonIconClosed`,
    menuButtonIconOpen: `${PREFIX}-menuButtonIconOpen`,
};

const StyledIconButton = styled(IconButton, {
    name: PREFIX,
    overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
    padding: 0,
    [`& .${SidebarToggleButtonClasses.menuButtonIconClosed}`]: {
        transition: theme.transitions.create(["transform"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        transform: "rotate(0deg)",
    },

    [`& .${SidebarToggleButtonClasses.menuButtonIconOpen}`]: {
        transition: theme.transitions.create(["transform"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        transform: "rotate(360deg)",
    },
}));
